import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PageHeader } from "@/components/dashboard/shared/PageHeader";
import { KpiCard } from "@/components/dashboard/shared/KpiCard";
import { KpiGrid } from "@/components/dashboard/shared/KpiGrid";
import { EmptyState } from "@/components/dashboard/shared/EmptyState";
import { dashboardStyles } from "@/components/dashboard/shared/dashboard-styles";
import { AdminPageSkeleton } from "@/components/admin/AdminPageSkeleton";
import { CampaignComposer, type CampaignFormState } from "@/components/admin/CampaignComposer";
import { DashboardHead } from "@/components/SEO/DashboardHead";
import {
  buildCampaignBodyHtml,
  emptyCampaignFields,
  type CampaignContentFields,
} from "@/lib/campaign-email";
import api from "@/utils/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Copy,
  Mail,
  MoreHorizontal,
  Plus,
  Search,
  Send,
  Trash2,
  Eye,
  MousePointer,
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

interface EmailCampaign {
  _id: string;
  name: string;
  subject: string;
  content: string;
  recipientType: CampaignFormState["recipientType"];
  customRecipients: Array<{ email: string; name: string }>;
  status: "draft" | "scheduled" | "sending" | "sent" | "failed";
  scheduledAt?: string;
  sentAt?: string;
  author?: { name: string; email: string };
  stats: {
    totalSent: number;
    delivered: number;
    opened: number;
    clicked: number;
    bounced: number;
    unsubscribed: number;
  };
  createdAt: string;
}

interface CampaignAnalytics {
  totalCampaigns: number;
  stats: {
    totalSent: number;
    delivered: number;
    opened: number;
    clicked: number;
    bounced: number;
    deliveryRate: number;
    openRate: number;
    clickRate: number;
  };
}

const blankForm = (): CampaignFormState => ({
  name: "",
  subject: "",
  content: buildCampaignBodyHtml(emptyCampaignFields()),
  recipientType: "store_owners",
  customRecipients: "",
  testEmail: "",
});

export function MarketingPage() {
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [analytics, setAnalytics] = useState<CampaignAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [form, setForm] = useState<CampaignFormState>(blankForm);
  const [fields, setFields] = useState<CampaignContentFields>(emptyCampaignFields());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<EmailCampaign | null>(null);
  const [pendingSend, setPendingSend] = useState<EmailCampaign | null>(null);

  useEffect(() => {
    void fetchCampaigns();
    void fetchAnalytics();
  }, [currentPage, searchTerm, statusFilter]);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: "10",
      });
      if (searchTerm) params.append("search", searchTerm);
      if (statusFilter !== "all") params.append("status", statusFilter);
      const { data } = await api.get(`/api/marketing?${params}`);
      setCampaigns(data.campaigns);
      setTotalPages(data.pagination.pages);
    } catch {
      toast.error("Failed to fetch email campaigns");
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const { data } = await api.get("/api/marketing/analytics");
      setAnalytics(data);
    } catch {
      /* analytics are optional */
    }
  };

  const parseRecipients = () =>
    form.customRecipients
      .split("\n")
      .map((line) => {
        const [name, email] = line.split(",").map((s) => s.trim());
        return { name: name || "", email: email || line.trim() };
      })
      .filter((recipient) => recipient.email);

  const openCreate = () => {
    const next = emptyCampaignFields();
    setEditingId(null);
    setFields(next);
    setForm({ ...blankForm(), content: buildCampaignBodyHtml(next) });
    setComposerOpen(true);
  };

  const openEdit = (campaign: EmailCampaign) => {
    setEditingId(campaign._id);
    setFields({
      ...emptyCampaignFields("simple"),
      headline: campaign.subject,
      body: campaign.content,
    });
    setForm({
      name: campaign.name,
      subject: campaign.subject,
      content: campaign.content,
      recipientType: campaign.recipientType,
      customRecipients: (campaign.customRecipients || [])
        .map((r) => `${r.name}, ${r.email}`)
        .join("\n"),
      testEmail: "",
    });
    setComposerOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.subject.trim() || !form.content.trim()) {
      toast.error("Name, subject, and content are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        subject: form.subject.trim(),
        content: form.content,
        recipientType: form.recipientType,
        customRecipients: parseRecipients(),
      };
      if (editingId) {
        await api.put(`/api/marketing/${editingId}`, payload);
        toast.success("Campaign saved");
      } else {
        const { data } = await api.post("/api/marketing", payload);
        setEditingId(data._id);
        toast.success("Draft created");
      }
      setComposerOpen(false);
      await fetchCampaigns();
      await fetchAnalytics();
    } catch {
      toast.error("Failed to save campaign");
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!form.testEmail.trim() || !form.subject.trim() || !form.content.trim()) {
      toast.error("Add a test email, subject, and content first");
      return;
    }
    setSendingTest(true);
    try {
      await api.post("/api/marketing/test", {
        to: form.testEmail.trim(),
        subject: form.subject.trim(),
        content: form.content,
      });
      toast.success("Test email sent");
    } catch {
      toast.error("Failed to send test email");
    } finally {
      setSendingTest(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await api.delete(`/api/marketing/${pendingDelete._id}`);
      toast.success("Campaign deleted");
      setPendingDelete(null);
      await fetchCampaigns();
      await fetchAnalytics();
    } catch {
      toast.error("Failed to delete campaign");
    }
  };

  const handleSend = async () => {
    if (!pendingSend) return;
    try {
      await api.post(`/api/marketing/${pendingSend._id}/send`);
      toast.success("Campaign is sending");
      setPendingSend(null);
      await fetchCampaigns();
      await fetchAnalytics();
    } catch {
      toast.error("Failed to send campaign");
    }
  };

  const handleDuplicate = async (campaignId: string) => {
    try {
      const { data } = await api.post(`/api/marketing/${campaignId}/duplicate`);
      toast.success("Campaign copied");
      await fetchCampaigns();
      openEdit(data);
    } catch {
      toast.error("Failed to duplicate campaign");
    }
  };

  const statusClass = (status: string) => {
    switch (status) {
      case "sent":
        return "bg-[#d2e4c4] text-primary";
      case "sending":
        return "bg-[#EDE4FB] text-primary";
      case "failed":
        return "bg-red-100 text-red-700";
      default:
        return "bg-[#F5F5F3] text-muted-foreground";
    }
  };

  const recipientLabel = (type: string) =>
    ({
      all_users: "All users",
      store_owners: "Store owners",
      managers: "Managers",
      customers: "Customers",
      custom: "Custom list",
    }[type] || type);

  const formatDate = (value?: string) =>
    value
      ? new Date(value).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "";

  if (composerOpen) {
    return (
      <>
        <DashboardHead pageName={editingId ? "Edit campaign" : "New campaign"} />
        <CampaignComposer
          title={editingId ? "Edit campaign" : "New campaign"}
          form={form}
          fields={fields}
          saving={saving}
          sendingTest={sendingTest}
          onChangeForm={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
          onChangeFields={setFields}
          onClose={() => setComposerOpen(false)}
          onSave={handleSave}
          onSendTest={handleSendTest}
        />
      </>
    );
  }

  if (loading && campaigns.length === 0) {
    return <AdminPageSkeleton kpiCount={4} />;
  }

  return (
    <div className="space-y-6">
      <DashboardHead pageName="Marketing" />
      <PageHeader
        title="Email campaigns"
        subtitle="Write once, preview the branded layout, then send to the right audience."
        actions={
          <Button
            onClick={openCreate}
            className="h-auto gap-1 rounded-full !px-4 !py-2 text-sm font-semibold shadow-none"
          >
            <Plus className="h-4 w-4" />
            New campaign
          </Button>
        }
      />

      <KpiGrid loading={!analytics} skeletonCount={4}>
        <KpiCard label="Campaigns sent" value={analytics?.totalCampaigns ?? 0} icon={<Mail />} />
        <KpiCard label="Emails delivered" value={analytics?.stats.delivered ?? 0} icon={<Send />} />
        <KpiCard
          label="Open rate"
          value={`${(analytics?.stats.openRate ?? 0).toFixed(1)}%`}
          icon={<Eye />}
        />
        <KpiCard
          label="Click rate"
          value={`${(analytics?.stats.clickRate ?? 0).toFixed(1)}%`}
          icon={<MousePointer />}
        />
      </KpiGrid>

      <div className={cn(dashboardStyles.filterCard, "flex flex-col gap-3 sm:flex-row")}>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search campaigns"
            value={searchTerm}
            onChange={(e) => {
              setCurrentPage(1);
              setSearchTerm(e.target.value);
            }}
            className={dashboardStyles.filterInput}
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(value) => {
            setCurrentPage(1);
            setStatusFilter(value);
          }}
        >
          <SelectTrigger className={cn(dashboardStyles.filterSelect, "w-full sm:w-44")}>
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sending">Sending</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {campaigns.length === 0 ? (
        <div className={dashboardStyles.sectionCard}>
          <EmptyState
            icon={<Mail className="h-5 w-5" />}
            title="No campaigns yet"
            description="Create a branded email with a template, images, and a live preview."
            action={{ label: "New campaign", onClick: openCreate }}
          />
        </div>
      ) : (
        <div className={dashboardStyles.listContainer}>
          {campaigns.map((campaign) => (
            <div key={campaign._id} className={dashboardStyles.listItem}>
              <button
                type="button"
                onClick={() => openEdit(campaign)}
                className="min-w-0 flex-1 text-left"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate text-sm font-heading font-semibold">{campaign.name}</p>
                  <Badge className={cn(dashboardStyles.badge, statusClass(campaign.status))}>
                    {campaign.status}
                  </Badge>
                </div>
                <p className="mt-1 truncate text-xs text-muted-foreground">{campaign.subject}</p>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {recipientLabel(campaign.recipientType)} · {formatDate(campaign.sentAt || campaign.createdAt)}
                  {campaign.status === "sent" ? ` · ${campaign.stats.delivered} delivered` : ""}
                </p>
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => openEdit(campaign)}>Edit</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleDuplicate(campaign._id)}>
                    <Copy className="mr-2 h-4 w-4" />
                    Duplicate
                  </DropdownMenuItem>
                  {campaign.status === "draft" && (
                    <DropdownMenuItem onClick={() => setPendingSend(campaign)}>
                      <Send className="mr-2 h-4 w-4" />
                      Send now
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => setPendingDelete(campaign)}
                    className="text-red-600"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className={dashboardStyles.paginationBar}>
          <Button
            variant="outline"
            size="sm"
            className={dashboardStyles.paginationBtn}
            onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            className={dashboardStyles.paginationBtn}
            onClick={() => setCurrentPage((page) => Math.min(page + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete?.name} will be removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingSend} onOpenChange={(open) => !open && setPendingSend(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send this campaign now?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingSend?.name} will go out to {recipientLabel(pendingSend?.recipientType || "")} in the branded Sqaleshop layout.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSend}>Send now</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
